import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

const styles = {
  releaseCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '1.5rem 2rem',
    marginBottom: '1.5rem',
  },
  versionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    marginBottom: '0.25rem',
  },
  versionTag: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#0b1d3a',
  },
  date: {
    fontSize: '0.9rem',
    color: '#666',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#0b1d3a',
    marginTop: '1.25rem',
    marginBottom: '0.5rem',
  },
  subTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#444',
    marginTop: '1rem',
    marginBottom: '0.4rem',
  },
  list: {
    margin: '0 0 0.5rem 1.25rem',
    padding: 0,
    lineHeight: 1.7,
  },
  callout: {
    background: '#f0f7f0',
    border: '1px solid #c3e0c3',
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    marginTop: '0.75rem',
    fontSize: '0.9rem',
    color: '#2a5a2a',
  },
  adminLabel: {
    display: 'inline-block',
    background: '#e8eaf6',
    color: '#3949ab',
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '4px',
    marginLeft: '0.5rem',
    verticalAlign: 'middle',
  },
};

/**
 * Release notes page. Add new releases at the top of the RELEASES array.
 * Each release has: version, date, and a render function for its content.
 */
const RELEASES = [
  {
    version: 'v1.0.19+35',
    date: '25 March 2026',
    content: () => (
      <>
        <h3 style={styles.sectionTitle}>What App Users Will See</h3>
        <ul style={styles.list}>
          <li>
            <strong>Live race following</strong> — share your race via QR code
            or deep link. Friends and coaches follow your progress mark by mark
            in real time from their own device.
          </li>
          <li>
            <strong>Race Day dashboard</strong> — see race status, class starts
            and competitor tracking in one place. Integrates with committee
            portal race management.
          </li>
          <li>
            <strong>iOS Live Activity</strong> — bearing and distance to the
            next mark on the Dynamic Island and Lock Screen, so you can glance
            without opening the app.
          </li>
          <li>
            <strong>Navigation engine rewrite</strong> — pure Dart state machine
            decoupled from the UI layer. More accurate mark detection, smoother
            auto-advance, and a foundation for future instrument integration.
          </li>
          <li>
            <strong>Tablet onboarding</strong> — welcome flow adapted for iPad
            screen sizes.
          </li>
          <li>
            <strong>Bug fixes</strong> — tablet detection now uses logical
            pixels (not physical), onboarding loop fix, NMEA test stability.
          </li>
        </ul>

        <h3 style={styles.subTitle}>Does This Affect Race Day?</h3>
        <div style={styles.callout}>
          <strong>Yes — new Race Day features.</strong> The Race Day dashboard
          and live race following are new capabilities that connect the
          committee portal to what sailors see on the water. Committees create
          and schedule races in the portal; competitors see live status in the
          app.
        </div>

        <h3 style={styles.sectionTitle}>
          Portal Changes
          <span style={styles.adminLabel}>Global Admin</span>
        </h3>
        <ul style={styles.list}>
          <li>
            <strong>Race Day Hub</strong> — create and schedule races, detail
            view with status overview for committee use.
          </li>
          <li>
            <strong>Live race viewer</strong> — public page with session token
            URL (no auth required) for sharing race progress with spectators.
          </li>
          <li>
            <strong>Release checklist improvements</strong> — Firebase/config
            phase, Firestore persistence, store release notes editors.
          </li>
          <li>
            <strong>App preview on Cloudflare Pages</strong> — every release
            version now has permanent web preview URLs pointing at both prod
            and staging Firebase.
          </li>
        </ul>
      </>
    ),
  },
  {
    version: 'v1.0.18+34',
    date: '16 March 2026',
    content: () => (
      <>
        <h3 style={styles.sectionTitle}>What App Users Will See</h3>
        <ul style={styles.list}>
          <li>
            <strong>iPad cockpit layout</strong> — on tablets the navigation
            dashboard becomes a split-screen cockpit: large DTW/BTW hero
            metrics on the left, course diagram and mark list on the right.
            Designed for glanceable use at the helm.
          </li>
          <li>
            <strong>Nav dashboard refresh</strong> — new course info header
            with wind bearing, VHF channel and start location. Enhanced mark
            tiles with rounding indicators, leg timers and next-leg wind
            context. Collapsible wind/mark controls for less clutter.
          </li>
          <li>
            <strong>Course diagram improvements</strong> — four chart modes
            (static/animated for course or mark focus), belt vs track rounding
            arcs, adjustable mark icon sizes, active-mark highlighting with
            opacity for passed/upcoming legs.
          </li>
          <li>
            <strong>New onboarding</strong> — welcome flow with
            location-based club discovery. Shows every launch until a sailing
            ground is selected. Includes an interest form for sailing grounds
            not yet on the platform.
          </li>
          <li>
            <strong>Custom courses</strong> — sailors can create their own mark
            sequences and navigate them end-to-end.
          </li>
          <li>
            <strong>Race timer presets</strong> — 5-minute and 4-minute gun
            buttons for quicker start alignment.
          </li>
          <li>
            <strong>Bug fixes</strong> — stale event data when switching
            seasons, layout overflow on smaller screens, settings text overflow.
          </li>
        </ul>

        <h3 style={styles.subTitle}>Does This Affect Race Day?</h3>
        <div style={styles.callout}>
          <strong>No changes to race day workflow.</strong> Course cards, race
          schedules, and the publish flow are unchanged. The iPad cockpit
          layout and course diagram improvements give sailors better at-helm
          navigation but don't affect club-published content. Custom courses
          are independent of club courses.
        </div>

        <h3 style={styles.sectionTitle}>
          Portal Changes
          <span style={styles.adminLabel}>Global Admin</span>
        </h3>
        <ul style={styles.list}>
          <li>
            <strong>Admin migration complete</strong> — the old admin site
            (<code>/admin/index.html</code>) has been fully replaced by this
            portal. All 9 tabs migrated: Clubs, Features, Interest, Products,
            Beta Requests, Content, Documents, Racetimes, Gating Model.
          </li>
          <li>
            <strong>New pages</strong>: GlobalAdmin home with club picker,
            Config Levers, split Club/Season management.
          </li>
          <li>
            <strong>Portal header redesign</strong> with logo and role label.
          </li>
          <li>
            <strong>Firebase App Check</strong> added for security.
          </li>
          <li>
            <strong>Course editor improvements</strong> — unified leg table
            with TWA/turn angles, chart with wind rose and start/finish lines.
          </li>
        </ul>
      </>
    ),
  },
];

export default function ReleaseNotes() {
  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/" title="Release Notes" />

        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          What's new in the RaceMarks app and committee portal.
        </p>

        {RELEASES.map((release) => (
          <div key={release.version} style={styles.releaseCard}>
            <div style={styles.versionHeader}>
              <span style={styles.versionTag}>{release.version}</span>
              <span style={styles.date}>{release.date}</span>
            </div>
            {release.content()}
          </div>
        ))}
      </div>
    </>
  );
}
